import { JSONObject } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';

import { merge } from '@/components';
import { Autosave, Flex, Heading, Logo, Text } from '@/components/core';
import { useAgentIcon, useGetAgentFromPath } from '@/hooks';
import { toModelId, useAutoUpdateModel, useSuspenseGetModel } from '@/hooks/api';
import { useModelConfigStore } from '@/store';
import { convertToFieldConfig, DivProps, formatDate, isEmpty } from '@/utils';

import { stripReservedKeys } from './utils';

export type ModelPanelHeaderProps = DivProps & {
  modelId: string;
};

const IModelPanelHeader = ({ modelId, className, ...rest }: ModelPanelHeaderProps) => {
  const { data: model } = useSuspenseGetModel(toModelId(modelId));
  const fields = useModelConfigStore((state) => state.fields);
  const metadata = useModelConfigStore((state) => state.metadata);
  const preprocessConfig = useModelConfigStore((state) => state.preprocessConfig);
  const postprocessConfig = useModelConfigStore((state) => state.postprocessConfig);

  const updates = useMemo(() => {
    return {
      fieldConfig: convertToFieldConfig(fields),
      metadata: metadata ? stripReservedKeys(metadata) : undefined,
      preprocessConfig: !isEmpty(preprocessConfig) ? preprocessConfig : undefined,
      postprocessConfig: !isEmpty(postprocessConfig) ? postprocessConfig : undefined,
    } as JSONObject;
  }, [fields, metadata, postprocessConfig, preprocessConfig]);

  const { isPending, isSuccess, error } = useAutoUpdateModel(1000, toModelId(modelId), updates);

  const { data: agent } = useGetAgentFromPath();
  const agentIcon = useAgentIcon({ agentId: agent?.agentId });

  return (
    <div className={merge('flex items-center gap-2', className)} {...rest}>
      <Logo variant="formatter" className="rounded-lg" size="xl">
        {agentIcon}
      </Logo>
      <Flex col>
        <Heading size="lg">Extract with AI</Heading>
        <Text variant="tertiary">Last updated: {formatDate(model.updatedTime ?? model.createdTime)}</Text>
      </Flex>
      <div className="grow" />
      <Autosave isPending={isPending} isSuccess={isSuccess} error={error} />
    </div>
  );
};

export const ModelPanelHeader = memo(IModelPanelHeader) as typeof IModelPanelHeader;
